// Signing in to a project's widget.
//
// Two different things call themselves "who is reporting this" in this SDK, and
// they are not the same claim:
//
//   identify()  — the host application telling us about its own signed-in user.
//                 Free, silent, and worth exactly as much as the application
//                 saying it. Nothing here challenges it.
//
//   signIn()    — the person in front of the browser answering a code we mailed
//                 to the address they typed. Costs them a round trip to their
//                 inbox, and is the only one that survives a project deciding
//                 it wants a real address on every report.
//
// Which of the two a project asks for is the project's own setting, read from
// the API rather than configured here — see loginState(). A widget that guessed
// would either hide a mandatory sign-in or put one in front of a project that
// never asked for it.
//
// The session is stored per project, because that is the boundary the server
// draws too: one live session per address per project. A browser on a page that
// mounts two projects' widgets is two independent sign-ins, and signing out of
// one leaves the other alone.

const KEY_PREFIX = 'bugcatch.session.';

const storageKey = (projectId) => `${KEY_PREFIX}${projectId}`;

/**
 * The same sessions, held for the life of the page.
 *
 * Not a cache — a fallback. localStorage is unavailable outright in some
 * privacy modes and on some embedded webviews, and without this a project that
 * requires sign-in would be unusable there: the code would be accepted and the
 * session immediately forgotten, so the report that follows is refused. Held
 * here, signing in still works for as long as the tab is open; all that is lost
 * is surviving a reload, which is the part storage was buying.
 */
const inMemory = new Map();

/**
 * The session for one project, or null.
 *
 * Storage can throw outright in private modes, and can hold something another
 * version of this SDK wrote — both come back as "not signed in" rather than an
 * exception in the host application's page.
 */
export function readSession(projectId) {
  if (!projectId) return null;
  try {
    const raw = window.localStorage?.getItem(storageKey(projectId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.token) return parsed;
    }
  } catch { /* storage unavailable, or holding something we did not write */ }
  return inMemory.get(projectId) || null;
}

export function writeSession(projectId, session) {
  if (!projectId || !session?.token) return;
  const held = {
    token: session.token,
    email: session.email || null,
    name: session.name || null,
  };
  inMemory.set(projectId, held);
  try {
    window.localStorage?.setItem(storageKey(projectId), JSON.stringify(held));
  } catch { /* the session just won't survive a reload */ }
}

export function clearSession(projectId) {
  if (!projectId) return;
  inMemory.delete(projectId);
  try {
    window.localStorage?.removeItem(storageKey(projectId));
  } catch { /* ignore */ }
}

/** The headers every call here sends: the publishable key, plus who we are. */
function headers({ apiKey, token }) {
  return {
    'Content-Type': 'application/json',
    'X-BugCatch-Key': apiKey,
    // Its own header rather than Authorization, which on these routes already
    // carries the API key. The two answer different questions and a request
    // carries both.
    ...(token ? { 'X-BugCatch-Session': token } : {}),
  };
}

async function readError(res) {
  try {
    const body = await res.json();
    const message = Array.isArray(body.message) ? body.message.join(', ') : body.message;
    if (message) return message;
  } catch { /* non-JSON error */ }
  return `Request failed (HTTP ${res.status})`;
}

/**
 * What this project asks of the reporter, and who this browser is signed in as.
 *
 *   { mode: 'off' | 'optional' | 'required',
 *     signedIn: { email, name } | null,
 *     signedOutReason: string | null }
 *
 * Asked each time the panel opens rather than cached for the page: the session
 * it reports on can end elsewhere — the same address signing in on a phone ends
 * the one on this laptop — and a widget holding a cached "signed in as you"
 * would only find out at the moment the report failed.
 *
 * Never throws. A deployment that has not been updated answers 404 here, and an
 * SDK that treated that as fatal would take the whole widget down over a
 * feature the project had not switched on; the answer is "off", which is what
 * every project looked like before this existed.
 */
export async function loginState({ apiBase, apiKey, projectId, token }) {
  const off = { mode: 'off', signedIn: null, signedOutReason: null };
  try {
    const res = await fetch(
      `${apiBase}/ingest/login?projectId=${encodeURIComponent(projectId)}`,
      { headers: headers({ apiKey, token }) },
    );
    if (!res.ok) return off;
    const body = await res.json();
    return {
      mode: body.mode === 'optional' || body.mode === 'required' ? body.mode : 'off',
      signedIn: body.signedIn || null,
      signedOutReason: body.signedOutReason || null,
    };
  } catch {
    return off;
  }
}

/** Step one: ask for a code. Throws with the server's sentence on refusal. */
export async function startSignIn({ apiBase, apiKey, projectId, email, name }) {
  const res = await fetch(`${apiBase}/ingest/login/start`, {
    method: 'POST',
    headers: headers({ apiKey }),
    body: JSON.stringify({ projectId, email, ...(name ? { name } : {}) }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
}

/**
 * Step two: spend the code.
 *
 * Stores the session on success, which is what makes it survive a reload and
 * what every other call here reads. Signing in ends this address's session on
 * this project everywhere else — that is the server's rule, not something this
 * browser can opt out of.
 */
export async function confirmSignIn({ apiBase, apiKey, projectId, email, code }) {
  const res = await fetch(`${apiBase}/ingest/login`, {
    method: 'POST',
    headers: headers({ apiKey }),
    body: JSON.stringify({ projectId, email, code }),
  });
  if (!res.ok) throw new Error(await readError(res));

  const body = await res.json();
  const session = { token: body.sessionToken, email: body.email, name: body.name };
  writeSession(projectId, session);
  return session;
}

/**
 * Sign out, here and on the server.
 *
 * Local storage is cleared whatever the network says. Somebody pressing sign
 * out on a shared machine has to end up signed out on it, and a fetch that
 * failed is not a reason to leave their address in the next person's widget.
 */
export async function endSignIn({ apiBase, apiKey, projectId, token }) {
  clearSession(projectId);
  if (!token) return { signedOut: true };
  try {
    await fetch(`${apiBase}/ingest/logout`, {
      method: 'POST',
      headers: headers({ apiKey, token }),
      body: JSON.stringify({ projectId }),
    });
  } catch { /* the token is already unusable from this browser */ }
  return { signedOut: true };
}
